import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Image as ImageIcon, ArrowRight } from "lucide-react";
import { BIZ } from "@/data/site";

/**
 * Premium before-and-after project carousel with drag-to-compare slider on
 * each slide. Each slide may include real `before`/`after` image URLs; when
 * absent, a labeled gradient placeholder is shown so the layout still holds.
 * User-controlled: no auto-advance.
 */
const SLIDES = [
  {
    label: "Driveway wash",
    city: "Goodlettsville, TN",
    desc: "Years of oil, tire marks, and mildew lifted with a rotating surface-cleaner pass — restored to original tan concrete color in one visit.",
    slug: "driveway-cleaning-goodlettsville-tn",
    before: "/portfolio/driveway-goodlettsville-before.webp",
    after: "/portfolio/driveway-goodlettsville-after.webp",
    aspect: "aspect-[4/5] md:aspect-[16/10]",
  },
  {
    label: "Patio wash",
    city: "Goodlettsville, TN",
    desc: "Heavy mildew, algae, and dirt lifted from a covered back patio — soft-wash + surface-cleaner combo brought the concrete back to bright tan.",
    slug: "concrete-patio-cleaning-goodlettsville-tn",
    before: "/portfolio/patio-goodlettsville-before.webp",
    after: "/portfolio/patio-goodlettsville-after.webp",
    aspect: "aspect-[4/5] md:aspect-[16/10]",
  },
  {
    label: "Gutter face wash",
    city: "Goodlettsville, TN",
    desc: "Years of mildew, algae, and tiger-stripe stains lifted from aluminum gutter faces — restored to bright factory white.",
    slug: "gutter-cleaning-goodlettsville-tn",
    before: "/portfolio/gutters-goodlettsville-before.jpg",
    after: "/portfolio/gutters-goodlettsville-after.jpg",
    aspect: "aspect-square md:aspect-[16/10]",
  },
  {
    label: "House soft wash",
    city: "Example project",
    desc: "Vinyl siding darkened by algae streaks and mildew brought back to a uniform factory finish with a low-pressure soft-wash — safe for seams, trim, and shutters.",
    slug: "house-soft-wash",
    before: "/portfolio/house-wash-stock-before.jpg",
    after: "/portfolio/house-wash-stock-after.jpg",
    aspect: "aspect-[4/5] md:aspect-[16/10]",
  },
  {
    label: "Roof soft wash",
    city: "Example project",
    desc: "Black gloeocapsa-magma streaks killed on contact with a soft-wash chemistry — no shingle damage, no granule loss, results visible within days.",
    slug: "roof-soft-wash",
    before: "/portfolio/roof-wash-stock-before.jpg",
    after: "/portfolio/roof-wash-stock-after.jpg",
    aspect: "aspect-[4/5] md:aspect-[16/10]",
  },
  {
    label: "Window cleaning",
    city: "Example project",
    desc: "Water-spots, pollen, and bug speckles cleared off exterior glass — streak-free, drip-free, lint-free finish.",
    slug: "window-cleaning",
    before: "/portfolio/window-cleaning-stock-before.jpg",
    after: "/portfolio/window-cleaning-stock-after.jpg",
    aspect: "aspect-square md:aspect-[16/10]",
  },
  {
    label: "Sidewalk wash",
    city: "Example project",
    desc: "Years of foot-traffic grime surface-cleaned off a residential walkway — slab-by-slab the concrete returns to its original tan.",
    slug: "sidewalk-wash",
    before: "/portfolio/sidewalk-stock-before.jpg",
    after: "/portfolio/sidewalk-stock-after.jpg",
    aspect: "aspect-[4/5] md:aspect-[16/10]",
  },
  {
    label: "Fence wash",
    city: "Example project",
    desc: "Weathered cedar fence boards stripped of mildew and UV grey — grain restored, perfect prep for stain or sealant.",
    slug: "fence-wash",
    before: "/portfolio/fence-stock-before.jpg",
    after: "/portfolio/fence-stock-after.jpg",
    aspect: "aspect-[4/5] md:aspect-[16/10]",
  },
  {
    label: "Deck wash",
    city: "Example project",
    desc: "Weathered pressure-treated deck boards stripped of greyed-out mildew — grain restored, perfect prep for stain or sealant.",
    slug: "deck-wash",
    before: "/portfolio/deck-stock-before.jpg",
    after: "/portfolio/deck-stock-after.jpg",
    aspect: "aspect-[4/5] md:aspect-[16/10]",
  },
  {
    label: "Commercial storefront",
    city: "Example project",
    desc: "Trafficked storefront entry concrete — gum stains and dark pathways surface-cleaned to a uniform professional finish.",
    slug: "commercial-storefront",
    before: "/portfolio/commercial-stock-before.jpg",
    after: "/portfolio/commercial-stock-after.jpg",
    aspect: "aspect-[4/5] md:aspect-[16/10]",
  },
];

export default function BeforeAfterCarousel() {
  const [i, setI] = useState(0);
  const trackRef = useRef(null);
  const goto = (n) => setI((n + SLIDES.length) % SLIDES.length);
  const slide = SLIDES[i];

  return (
    <section
      id="our-work"
      data-testid="before-after-carousel"
      className="py-20 md:py-28 bg-white scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-3">
              Our work
            </div>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-stone-900 tracking-tight leading-tight"
              style={{ fontFamily: "Outfit" }}
            >
              Before &amp; after,<br className="hidden md:block" /> across {BIZ.region}.
            </h2>
          </div>
          <Link
            to="/pressure-washing-gallery/"
            className="text-sm font-bold text-brand-700 hover:text-electric-500 inline-flex items-center gap-2"
          >
            View full gallery <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

          {/* Slide */}
        <div ref={trackRef} className="rounded-3xl overflow-hidden border border-stone-200 shadow-lg bg-stone-50">
          <div className="relative">
            <BeforeAfterCompare
              before={slide.before}
              after={slide.after}
              label={slide.label}
              aspect={slide.aspect || "aspect-[16/9]"}
            />
            <div className="absolute top-4 left-4 rounded-full bg-white/95 backdrop-blur px-3 py-1 text-xs font-bold text-stone-900 shadow">
              {slide.label} <span className="text-stone-500 font-medium">· {slide.city}</span>
            </div>
          </div>

          <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
            <p className="text-stone-700 leading-relaxed">{slide.desc}</p>
            {SLIDES.length > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => goto(i - 1)}
                  data-testid="carousel-prev"
                  className="p-3 rounded-full border border-stone-300 hover:bg-stone-100 focus:outline-none focus:ring-2 focus:ring-electric-500"
                  aria-label="Previous project"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-1.5 px-2" data-testid="carousel-dots">
                  {SLIDES.map((s, idx) => (
                    <button
                      key={s.slug || s.label}
                      type="button"
                      onClick={() => goto(idx)}
                      aria-label={`Go to ${s.label}`}
                      aria-current={idx === i}
                      className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-electric-500" : "w-1.5 bg-stone-300 hover:bg-stone-400"}`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => goto(i + 1)}
                  data-testid="carousel-next"
                  className="p-3 rounded-full border border-stone-300 hover:bg-stone-100 focus:outline-none focus:ring-2 focus:ring-electric-500"
                  aria-label="Next project"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// Drag-to-compare — pure CSS/JS, no external lib.
function BeforeAfterCompare({ before, after, label, aspect = "aspect-[16/9]" }) {
  const [pos, setPos] = useState(50);
  const onMove = (clientX, rect) => {
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, x)));
  };
  const ref = useRef(null);
  return (
    <div
      ref={ref}
      className={`relative ${aspect} select-none touch-pan-y bg-stone-200 cursor-ew-resize overflow-hidden`}
      role="slider"
      aria-label={`Drag to compare ${label || "before and after"}`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 5));
        if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 5));
      }}
      onMouseMove={(e) => e.buttons === 1 && ref.current && onMove(e.clientX, ref.current.getBoundingClientRect())}
      onTouchMove={(e) => ref.current && onMove(e.touches[0].clientX, ref.current.getBoundingClientRect())}
      onClick={(e) => ref.current && onMove(e.clientX, ref.current.getBoundingClientRect())}
    >
      {/* AFTER — full */}
      {after ? (
        <img
          src={after}
          alt={`After — ${label || "project"}`}
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-electric-100 via-brand-100 to-white flex items-center justify-center text-brand-700">
          <div className="text-center">
            <ImageIcon className="w-12 h-12 mx-auto opacity-60" />
            <div className="text-[10px] mt-2 uppercase tracking-widest font-bold opacity-70">[after photo]</div>
          </div>
        </div>
      )}
      {/* BEFORE — clipped */}
      <div
        className="absolute inset-0"
        style={{ clipPath: `polygon(0 0, ${pos}% 0, ${pos}% 100%, 0 100%)` }}
      >
        {before ? (
          <img
            src={before}
            alt={`Before — ${label || "project"}`}
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-stone-500 to-stone-700 flex items-center justify-center text-stone-200">
            <div className="text-center">
              <ImageIcon className="w-12 h-12 mx-auto opacity-60" />
              <div className="text-[10px] mt-2 uppercase tracking-widest font-bold opacity-80">[before photo]</div>
            </div>
          </div>
        )}
      </div>
      {/* Handle */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white shadow-lg pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow-lg flex items-center justify-center">
          <div className="flex gap-0.5">
            <ChevronLeft className="w-3 h-3 text-brand-700" />
            <ChevronRight className="w-3 h-3 text-brand-700" />
          </div>
        </div>
      </div>
      {/* Labels */}
      <div className="absolute bottom-3 left-3 text-[10px] uppercase tracking-widest text-white font-bold bg-black/60 rounded px-2 py-1">Before</div>
      <div className="absolute bottom-3 right-3 text-[10px] uppercase tracking-widest text-white font-bold bg-brand-600 rounded px-2 py-1">After</div>
    </div>
  );
}
